import React, { useState, useEffect, useRef } from 'react';
import { LoadingSpinner } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import ComponentFactory from './PageComponentFactory';
import styles from './Page.module.less';
import ServerErrorPage from '../components/App/ServerErrorPage';
import SEO from '../components/App/SEO';
import clientLogger from '../lib/logger';
import { trackPageShown, trackPageFocus, trackPageLostFocus } from '../lib/fpti';
import { getScrollOffset } from '../util/hashNavigation';
import NotFound from './404';
import usePrevious from '../util/usePreviousHook';
import { useSelector } from 'react-redux';
import { previewSelector } from '../store/App/selectors';
import { useParams } from 'react-router';
import { addCopyListenerToCodeBlocks } from '../util/codeBlockKeyboardCopy';

const Page = ({ ecmContent, serviceData }) => {
  const url = useLocation().pathname;
  const { hash } = useLocation();
  const { country } = useParams();

  const [initialState, setInitialState] = useState(ecmContent);
  const [lastUpdated, setLastUpdated] = useState(latestUpdateTime(ecmContent));
  const [loading, setLoading] = useState(false);
  const [pageNotFound, setPageNotFound] = useState(false);
  const [error, setError] = useState(false);
  const controllerRef = useRef(null);
  const previousLoading = usePrevious(loading);
  const isPreview = useSelector(previewSelector);
  let isReloading = false;

  function latestUpdateTime(content) {
    if (content && content.pageContent) {
      return new Date(Math.max(...content.pageContent.map((entry) => new Date(entry.update_time))));
    }
  }

  useEffect(() => {
    // Needed to handle anchors when navigating client side
    if (hash) {
      window.location.href = hash;
    }
  }, [hash, loading]);

  useEffect(() => {
    window.fpti.page = `studio${url.replace(/\//g, '_')}`;
    if (country) {
      window.fpti.country_selected = country;
    }
    document.documentElement.style.scrollPaddingTop = `${getScrollOffset(url)}px`;

    // Reseting the FPTI scroll instance on client side navigation
    window.PAYPAL?.analytics?.instance?.resetScroll();
  }, [url]);

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      isReloading = true;
    });
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      if (isReloading) {
        isReloading = false;
        return;
      }
      if (document.visibilityState === 'visible') {
        trackPageFocus(url);
      } else {
        trackPageLostFocus(url);
      }
    });
  }, []);

  useEffect(() => {
    // URL might get changed in between ECM content is loading, aborting the stale call
    if (controllerRef.current) {
      setLoading(false);
      controllerRef.current.abort();
    }
    if (!initialState || initialState.pageMetaData.url !== url) {
      controllerRef.current = new AbortController();
      setLoading(true);
      const fetchEcmContent = async () => {
        try {
          const baseUrl = '/studio/api/v1/ecm/data';
          const urlObject = new URL(baseUrl, window.location.origin);

          urlObject.searchParams.append('url', url);
          if (isPreview) {
            urlObject.searchParams.append('mode', 'preview');
          }

          const response = await fetch(urlObject.toString(), {
            signal: controllerRef.current.signal
          });
          const result = await response.json();
          if (response.ok && result.data) {
            setInitialState(result.data);
            setLastUpdated(latestUpdateTime(result.data));
            setPageNotFound(false);
            setError(false);
            trackPageShown(url);
          } else if (response.status === 404 || !result.data) {
            setPageNotFound(true);
          } else {
            setError(true);
            clientLogger.error('PAGE:LOAD:ERROR', { error: result.body, status: response.status });
          }
          setLoading(false);
        } catch (err) {
          if (err.name !== 'AbortError') {
            setError(true);
            setLoading(false);
          }
        }
      };
      fetchEcmContent();
    } else {
      setPageNotFound(false);
      setError(false);
    }
  }, [url]);

  useEffect(() => {
    addCopyListenerToCodeBlocks();
    if (previousLoading === true && loading === false) {
      const updateScrollPadding = () => {
        document.documentElement.style.scrollPaddingTop = `${getScrollOffset(url)}px`;
      };
      // waiting for DOM to render
      const timeout = setTimeout(updateScrollPadding, 100);
      return () => clearTimeout(timeout);
    }
  }, [loading, url]);

  if (error) {
    return <ServerErrorPage />;
  }

  if (pageNotFound) {
    return <NotFound />;
  }

  if (loading) {
    return (
      <div className={styles.page_loader_container}>
        <div className={styles.page_loader}>
          <LoadingSpinner screenReaderText="loading" size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page_container}>
      <SEO pathName={url} ecmContent={initialState?.pageMetaData} />
      {initialState?.pageContent.map((content) => {
        return (
          <ComponentFactory
            key={content.id}
            latestUpdateTime={lastUpdated}
            pageContent={content}
            serviceData={serviceData}
            pageMetaDataConfig={initialState?.pageMetaData?.config}
          />
        );
      })}
    </div>
  );
};

Page.propTypes = {
  ecmContent: PropTypes.object,
  serviceData: PropTypes.objectOf({
    apps: PropTypes.array.isRequired,
    clientAccessToken: PropTypes.string,
    defaultClientId: PropTypes.string
  }).isRequired
};
Page.defaultProps = { ecmContent: null };

export default Page;
